import {
  Container,
  Input,
  InputContainer,
  Mic,
  SendButton,
} from "./styles";

import sendIcon from "../../assets/icons/send-icon.svg";
import micIcon from "../../assets/icons/microphone.svg";

import { useEffect, useRef, useState } from "react";
import { pipeline, env } from "@xenova/transformers";
import { AnimationData } from "../../assets/glb/animations";
import { getAnimationByTokens } from "../../utils/animationHandle";
import {
  Conversation,
  MessageType,
  generateTitle,
  saveConversation,
} from "../../utils/conversationStorage";
import { processMensagem } from "../../service/dynamicCommunicationService";
import { ConversationMessage } from "../../service/openaiService";

env.allowLocalModels = false;
env.useBrowserCache = false;
(env as any).logLevel = "error";
env.backends.onnx.wasm.numThreads = 1;

interface AIComponentProps {
  setAnimation: React.Dispatch<React.SetStateAction<AnimationData[]>>;
  currentConversation: Conversation;
  setCurrentConversation: React.Dispatch<React.SetStateAction<Conversation>>;
  activeConversation: Conversation | null;
  onConversationChange: () => void;
  presetText?: string;
  onPresetConsumed?: () => void;
  isProcessing: boolean;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
}

const AiInput = ({
  setAnimation,
  currentConversation,
  setCurrentConversation,
  activeConversation,
  onConversationChange,
  presetText,
  onPresetConsumed,
  isProcessing,
  setIsProcessing,
}: AIComponentProps) => {
  const [inputValue, setInputValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const transcriberRef = useRef<any>(null);

  const messages = currentConversation.messages;

  useEffect(() => {
    const loadAnimation = async () => {
      const hiAnimation = await getAnimationByTokens("OLA");
      setAnimation(hiAnimation);
    };
    loadAnimation();
  }, []);

  useEffect(() => {
  if (presetText) {
    setInputValue(presetText);
    onPresetConsumed?.();
  }
}, [presetText]);

  useEffect(() => {
    if (activeConversation) {
      setCurrentConversation(activeConversation);
    }
  }, [activeConversation]);

  useEffect(() => {
    const hasUserMessage = messages.some((m) => m.sender === "user");
    if (!hasUserMessage) return;

    const title = generateTitle(messages);
    const updated: Conversation = { ...currentConversation, title };
    saveConversation(updated);
  }, [messages]);

  const startRecognition = async () => {

    if (isProcessing) {
      return;
    }

    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      setIsRecording(true);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          setIsProcessing(true);

          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioContext = new AudioContext({ sampleRate: 16000 });
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const float32 = audioBuffer.getChannelData(0);

          transcriberRef.current = await pipeline(
            "automatic-speech-recognition",
            "Xenova/whisper-small"
          );

          const result = await transcriberRef.current(float32, {
            language: "pt",
            task: "transcribe",
          });

          setInputValue(result?.text || "");
        } catch (err) {
          console.error("Erro ao transcrever áudio:", err);
        } finally {
          setIsProcessing(false);
        }
      };

      mediaRecorder.start();
      setTimeout(() => {
        mediaRecorder.stop();
        setIsRecording(false);
      }, 5000);
    } catch (err) {
      console.error("Erro no microfone:", err);
      setIsRecording(false);
    }
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    onConversationChange();

    const userMessage: MessageType = {
      time: new Date(),
      text: trimmed,
      sender: "user",
    };

    // Adiciona mensagem do usuário imediatamente
    setCurrentConversation((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
    }));

    setInputValue("");
    setIsProcessing(true);

    let lisaReplyText = "Desculpe, não consegui processar sua mensagem no momento. Por favor, tente novamente.";
    let animationsToPlay: string[] = [];

    try {
      // Converte o histórico atual para o formato do GPT
      const conversationHistory: ConversationMessage[] = currentConversation.messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Usa GPT com histórico
      console.log('Processando...');
      const resultado = await processMensagem(trimmed, conversationHistory);
      
      lisaReplyText = resultado.resposta_pt;
      
      // Filtra animações válidas (remove NO_ANIMATION, mas mantém datilologia)
      animationsToPlay = resultado.animacoes
        .filter(a => a.animacao !== 'NO_ANIMATION')
        .map(a => a.animacao);
      
      console.log('GPT respondeu:', resultado);
      console.log('Animações válidas:', animationsToPlay);
      
      // Log de palavras soletradas
      const soletradas = resultado.animacoes.filter(a => a.datilologia);
      if (soletradas.length > 0) {
        console.log('Palavras soletradas:', soletradas.map(a => a.palavra).join(', '));
      }
      
      // Reproduz animações apenas se houver alguma válida
      if (animationsToPlay.length > 0) {
        const animations = await getAnimationByTokens(animationsToPlay.join(' '));
        setAnimation(animations);
      } else {
        console.warn('Nenhuma animação válida encontrada (todas abaixo do limiar)');
      }
    } catch (error) {
      console.error('Erro ao processar com GPT:', error);
      lisaReplyText = "Desculpe, não consegui processar sua mensagem no momento. Por favor, tente novamente.";
    } finally {
      setIsProcessing(false);
    }

    const lisaReply: MessageType = {
      time: new Date(),
      text: lisaReplyText,
      sender: "lisa",
      glosas: animationsToPlay,
    };

    setCurrentConversation((prev) => ({
      ...prev,
      messages: [...prev.messages, lisaReply],
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <Container>
      <InputContainer data-tutorial="chat-input">
        <Input
          value={isProcessing ? "Processando..." : inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          type="text"
          placeholder="Pergunte qualquer coisa"
          onKeyDown={handleKeyDown}
          tabIndex={4}
          disabled={isProcessing}
        />
        <Mic $recording={isRecording} $processing={isProcessing} onClick={startRecognition} className="hint" data-hint="Envio por Voz"
            data-hint-placement="top" tabIndex={5} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startRecognition(); }}}>
          <img src={micIcon} alt="microfone" />
        </Mic>
        <SendButton 
          onClick={handleSend}  
          tabIndex={6} 
          onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSend(); }}}
          disabled={isProcessing}
        >
          {isProcessing ? <div className="btnSpinner" /> : "Enviar"}
        </SendButton>
      </InputContainer>
    </Container>
  );
};

export default AiInput;